import { supabase } from '../lib/supabase';

export const dashboardService = {
  // Get total document count
  async getTotalDocuments() {
    try {
      const { count, error } = await supabase?.from('documents')?.select('*', { count: 'exact', head: true })?.eq('status', 'active');

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching document count:', error);
      throw error;
    }
  },

  // Get active user count - fixed to accurately count active users
  async getActiveUsers() {
    try {
      const { count, error } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true })?.eq('is_active', true);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching user count:', error);
      throw error;
    }
  },

  // Get pending change requests count
  async getPendingChanges() {
    try {
      const { count, error } = await supabase?.from('change_requests')?.select('*', { count: 'exact', head: true })?.eq('status', 'pending');

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching pending changes count:', error);
      throw error;
    }
  },

  // Get storage usage from documents
  async getStorageUsed() {
    try {
      const { data, error } = await supabase?.from('documents')?.select('file_size')?.eq('status', 'active');

      if (error) throw error;

      // Calculate total file size in bytes
      const totalBytes = data?.reduce((sum, doc) => sum + (doc?.file_size || 0), 0) || 0;
      
      // Convert to appropriate units
      const totalMB = totalBytes / (1024 * 1024);
      const totalGB = totalBytes / (1024 * 1024 * 1024);
      
      return {
        bytes: totalBytes,
        mb: totalMB,
        gb: totalGB,
        formatted: totalGB >= 1 ? `${totalGB?.toFixed(2)} GB` : `${totalMB?.toFixed(2)} MB`
      };
    } catch (error) {
      console.error('Error fetching storage usage:', error);
      throw error;
    }
  },

  // Get recent activities (document uploads, changes)
  async getRecentActivities(limit = 10) {
    try {
      // Get recent document uploads
      const { data: documents, error: docError } = await supabase?.from('documents')?.select(`
          id,
          name,
          created_at,
          owner_id,
          user_profiles!documents_owner_id_fkey (
            full_name,
            email
          )
        `)?.order('created_at', { ascending: false })?.limit(limit);

      if (docError) throw docError;

      // Get recent change requests
      const { data: changes, error: changeError } = await supabase?.from('change_requests')?.select(`
          id,
          title,
          status,
          requested_at,
          requested_by,
          user_profiles!change_requests_requested_by_fkey (
            full_name,
            email
          )
        `)?.order('requested_at', { ascending: false })?.limit(limit);

      if (changeError) throw changeError;

      // Combine and format activities
      const activities = [];

      // Add document activities
      documents?.forEach(doc => {
        activities?.push({
          id: `doc-${doc?.id}`,
          type: 'document_upload',
          title: `Document uploaded: ${doc?.name}`,
          user: doc?.user_profiles?.full_name || 'Unknown User',
          timestamp: doc?.created_at,
          icon: 'Upload'
        });
      });

      // Add change request activities
      changes?.forEach(change => {
        activities?.push({
          id: `change-${change?.id}`,
          type: 'change_request',
          title: `Change request: ${change?.title}`,
          user: change?.user_profiles?.full_name || 'Unknown User',
          timestamp: change?.requested_at,
          status: change?.status,
          icon: 'GitBranch'
        });
      });

      // Sort by timestamp and limit
      return activities?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))?.slice(0, limit);

    } catch (error) {
      console.error('Error fetching recent activities:', error);
      throw error;
    }
  },

  // Get pending approvals for current user
  async getPendingApprovals(limit = 5) {
    try {
      const { data, error } = await supabase?.from('change_requests')?.select(`
          id,
          title,
          description,
          request_type,
          requested_at,
          requested_by,
          user_profiles!change_requests_requested_by_fkey (
            full_name,
            email
          )
        `)?.eq('status', 'pending')?.order('requested_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(request => ({
        id: request?.id,
        title: request?.title,
        description: request?.description,
        type: request?.request_type,
        requestedBy: request?.user_profiles?.full_name || 'Unknown User',
        requestedAt: request?.requested_at,
        priority: 'medium' // Default priority
      })) || [];

    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      throw error;
    }
  },

  // Get all dashboard metrics at once with better error handling
  async getDashboardMetrics() {
    try {
      const [
        totalDocuments,
        activeUsers,
        pendingChanges,
        storageUsed,
        recentActivities,
        pendingApprovals
      ] = await Promise.allSettled([
        this.getTotalDocuments(),
        this.getActiveUsers(),
        this.getPendingChanges(),
        this.getStorageUsed(),
        this.getRecentActivities(8),
        this.getPendingApprovals(5)
      ]);

      // Extract values from settled promises, using defaults for failures
      return {
        totalDocuments: totalDocuments?.status === 'fulfilled' ? totalDocuments?.value : 0,
        activeUsers: activeUsers?.status === 'fulfilled' ? activeUsers?.value : 0,
        pendingChanges: pendingChanges?.status === 'fulfilled' ? pendingChanges?.value : 0,
        storageUsed: storageUsed?.status === 'fulfilled' ? storageUsed?.value : { formatted: '0 MB', gb: 0 },
        recentActivities: recentActivities?.status === 'fulfilled' ? recentActivities?.value : [],
        pendingApprovals: pendingApprovals?.status === 'fulfilled' ? pendingApprovals?.value : []
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  }
};